declare const USER_ID_BRAND: unique symbol;
declare const ACCOUNT_SESSION_ID_BRAND: unique symbol;
declare const GAME_SESSION_ID_BRAND: unique symbol;
declare const PRESENCE_ID_BRAND: unique symbol;
declare const VALIDATED_ACCOUNT_SESSION_BRAND: unique symbol;

/** Durable account identity. This identifier is never part of a browser DTO. */
export type UserId = string & { readonly [USER_ID_BRAND]: 'UserId' };

/** Account-cookie session identifier; distinct from gameplay and browser-presence identifiers. */
export type AccountSessionId = string & {
  readonly [ACCOUNT_SESSION_ID_BRAND]: 'AccountSessionId';
};

/** Durable game reconnect session identifier. It is not an account credential. */
export type GameSessionId = string & { readonly [GAME_SESSION_ID_BRAND]: 'GameSessionId' };

/** Process-local browser-tab presence identifier. It grants no account or game authority. */
export type PresenceId = string & { readonly [PRESENCE_ID_BRAND]: 'PresenceId' };

export type AuthPolicy = 'disabled' | 'optional' | 'required';
export type LoginIssuance = 'available' | 'disabled' | 'degraded';

export const LEAN_AUTH_ROUTES = Object.freeze([
  { method: 'GET', path: '/api/runtime-config' },
  { method: 'POST', path: '/api/auth/google/intent' },
  { method: 'POST', path: '/api/auth/google/callback' },
  { method: 'GET', path: '/api/auth/session' },
  { method: 'PATCH', path: '/api/users/me' },
  { method: 'POST', path: '/api/auth/logout' },
] as const);

export type AccountProfile =
  | { status: 'pending_profile'; displayName: null }
  | { status: 'active'; displayName: string };

/** Server-only link between an account and its canonical external subject. */
export interface ExternalIdentity {
  userId: UserId;
  provider: string;
  issuer: string;
  providerSubject: string;
}

/** Durable server-only account session record. Raw cookie bearer material is absent. */
export interface AccountSession {
  accountSessionId: AccountSessionId;
  userId: UserId;
  profile: AccountProfile;
  tokenDigest: Uint8Array;
  idleExpiresAt: Date;
  absoluteExpiresAt: Date;
  revokedAt: Date | null;
}

/** Session record after digest, revocation and both TTL checks have succeeded. */
export interface ValidatedAccountSession {
  readonly [VALIDATED_ACCOUNT_SESSION_BRAND]: true;
  accountSessionId: AccountSessionId;
  userId: UserId;
  profile: AccountProfile;
}

/** Active-profile authority consumed by HTTP/WS gameplay seams. */
export interface AuthenticatedPrincipal {
  userId: UserId;
  accountSessionId: AccountSessionId;
  userStatus: 'active';
  displayName: string;
}

export interface PublicRuntimeConfig {
  authPolicy: AuthPolicy;
  loginIssuance: LoginIssuance;
  maintenanceEnabled?: boolean;
  assistantEnabled?: boolean;
}

/**
 * Browser session state. `csrfToken` is kept in memory by the browser and echoed only
 * on same-origin account mutations; it is not an account-session identifier.
 */
export type AuthSessionProjection =
  | {
      authentication: 'authenticated';
      accountStatus: 'pending_profile';
      profile: null;
      csrfToken: string;
    }
  | {
      authentication: 'authenticated';
      accountStatus: 'active';
      profile: { displayName: string };
      csrfToken: string;
    };

export type ActiveAuthSessionProjection = Extract<
  AuthSessionProjection,
  { accountStatus: 'active' }
>;

export interface GoogleLoginIntentRequest {
  returnTo: string;
}

/** Transient GIS values; the browser keeps them in memory until the popup callback completes. */
export interface GoogleLoginIntent {
  clientId: string;
  loginUri: string;
  state: string;
  nonce: string;
  returnTo: string;
}

export interface UpdateProfileRequest {
  displayName: string;
}

/** Narrow fakeable browser boundary for the lean account surface. */
export interface LeanAuthHttpAdapter {
  getRuntimeConfig(): Promise<PublicRuntimeConfig>;
  getSession(): Promise<AuthSessionProjection | null>;
  createGoogleLoginIntent(input: GoogleLoginIntentRequest): Promise<GoogleLoginIntent>;
  updateProfile(
    input: UpdateProfileRequest,
    csrfToken: string,
  ): Promise<ActiveAuthSessionProjection>;
  logoutCurrent(csrfToken: string): Promise<void>;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;

function parseCanonicalUuid(value: unknown, name: string): string {
  if (typeof value !== 'string' || !UUID_PATTERN.test(value)) {
    throw new Error(`${name} должен быть UUID в canonical lowercase-форме`);
  }
  return value;
}

export function parseUserId(value: unknown): UserId {
  return parseCanonicalUuid(value, 'userId') as UserId;
}

export function parseAccountSessionId(value: unknown): AccountSessionId {
  return parseCanonicalUuid(value, 'accountSessionId') as AccountSessionId;
}

export function parseGameSessionId(value: unknown): GameSessionId {
  return parseCanonicalUuid(value, 'gameSessionId') as GameSessionId;
}

export function parsePresenceId(value: unknown): PresenceId {
  return parseCanonicalUuid(value, 'presenceId') as PresenceId;
}

export function createExternalIdentity(input: ExternalIdentity): ExternalIdentity {
  return {
    userId: input.userId,
    provider: parseBoundedString(input.provider, 'external identity.provider', 32),
    issuer: parseBoundedString(input.issuer, 'external identity.issuer', 2_048),
    providerSubject: parseBoundedString(
      input.providerSubject,
      'external identity.providerSubject',
      255,
    ),
  };
}

function digestMatches(left: Uint8Array, right: Uint8Array): boolean {
  if (left.byteLength !== 32 || right.byteLength !== 32) return false;
  let difference = 0;
  for (let index = 0; index < left.byteLength; index += 1) {
    difference |= left[index]! ^ right[index]!;
  }
  return difference === 0;
}

function validDate(value: Date): boolean {
  return value instanceof Date && Number.isFinite(value.getTime());
}

/**
 * Turns a repository record into authority only after all account-session
 * validity checks. Callers pass the digest derived from the presented cookie,
 * never the raw bearer.
 */
export function validateAccountSession(
  session: AccountSession,
  presentedTokenDigest: Uint8Array,
  now: Date,
): ValidatedAccountSession | null {
  if (!validDate(now)
    || !validDate(session.idleExpiresAt)
    || !validDate(session.absoluteExpiresAt)
    || (session.revokedAt !== null && !validDate(session.revokedAt))) {
    throw new Error('account session содержит некорректный timestamp');
  }
  if (!digestMatches(session.tokenDigest, presentedTokenDigest)
    || session.revokedAt !== null
    || session.idleExpiresAt.getTime() <= now.getTime()
    || session.absoluteExpiresAt.getTime() <= now.getTime()) {
    return null;
  }
  return {
    accountSessionId: session.accountSessionId,
    userId: session.userId,
    profile: session.profile,
  } as ValidatedAccountSession;
}

export function authenticatedPrincipalFrom(
  session: ValidatedAccountSession,
): AuthenticatedPrincipal | null {
  if (session.profile.status !== 'active') return null;
  if (session.profile.displayName.length === 0) {
    throw new Error('active profile должен содержать displayName');
  }
  return {
    userId: session.userId,
    accountSessionId: session.accountSessionId,
    userStatus: 'active',
    displayName: session.profile.displayName,
  };
}

export function projectAuthSession(
  session: ValidatedAccountSession,
  csrfTokenInput: string,
): AuthSessionProjection {
  const csrfToken = parseBoundedString(csrfTokenInput, 'csrfToken', 512);
  if (session.profile.status === 'pending_profile') {
    return {
      authentication: 'authenticated',
      accountStatus: 'pending_profile',
      profile: null,
      csrfToken,
    };
  }
  if (session.profile.displayName.length === 0) {
    throw new Error('active profile должен содержать displayName');
  }
  return {
    authentication: 'authenticated',
    accountStatus: 'active',
    profile: { displayName: session.profile.displayName },
    csrfToken,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function assertOnlyKeys(
  value: Record<string, unknown>,
  allowed: readonly string[],
  name: string,
): void {
  const unknownKey = Object.keys(value).find((key) => !allowed.includes(key));
  if (unknownKey !== undefined) throw new Error(`${name} содержит неизвестное поле "${unknownKey}"`);
}

const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/u;

function parseBoundedString(value: unknown, name: string, maximumLength: number): string {
  if (typeof value !== 'string'
    || value.length === 0
    || value.length > maximumLength
    || CONTROL_CHARACTER_PATTERN.test(value)) {
    throw new Error(
      `${name} должен быть непустой строкой без control characters длиной до ${maximumLength}`,
    );
  }
  return value;
}

/**
 * Keep auth redirects on exact same-origin application entry points; expanding
 * this allowlist requires an explicit contract change and matching SPA route.
 */
export function parseAuthReturnTo(value: unknown): string {
  const returnTo = parseBoundedString(value, 'returnTo', 512);
  if (returnTo === '/decks' || returnTo === '/challenges') return returnTo;
  if (!returnTo.startsWith('/') || returnTo.startsWith('//') || returnTo.includes('\\')) {
    throw new Error('returnTo должен быть относительным same-origin path');
  }
  let parsed: URL;
  try {
    parsed = new URL(returnTo, 'https://cards.invalid');
  } catch {
    throw new Error('returnTo должен быть корректным relative URL');
  }
  if (parsed.origin !== 'https://cards.invalid' || parsed.hash !== '') {
    throw new Error('returnTo должен вести на разрешённый game path без fragment');
  }
  if (parsed.pathname !== '/') {
    throw new Error('returnTo должен вести на разрешённый game path без fragment');
  }
  const keys = [...parsed.searchParams.keys()];
  if (new Set(keys).size !== keys.length
    || keys.some((key) => !['room', 'playerId', 'quick', 'solo'].includes(key))) {
    throw new Error('returnTo содержит неизвестные или duplicate query keys');
  }
  const room = parsed.searchParams.get('room');
  const playerId = parsed.searchParams.get('playerId');
  const quick = parsed.searchParams.get('quick');
  const solo = parsed.searchParams.get('solo');
  const isLobby = keys.length === 0;
  const isPrivate = keys.length === 2 && room !== null
    && /^[A-Za-z0-9_-]{1,64}$/u.test(room)
    && (playerId === 'A' || playerId === 'B');
  const isQuick = keys.length === 1 && quick === '1';
  const isSolo = keys.length === 1 && solo === '1';
  if (!isLobby && !isPrivate && !isQuick && !isSolo) {
    throw new Error('returnTo содержит недопустимую game route комбинацию');
  }
  if (isLobby) return '/';
  if (isPrivate) return `/?room=${encodeURIComponent(room!)}&playerId=${playerId}`;
  if (isQuick) return '/?quick=1';
  return '/?solo=1';
}

export function parsePublicRuntimeConfig(value: unknown): PublicRuntimeConfig {
  if (!isRecord(value)) throw new Error('runtime config должен быть объектом');
  assertOnlyKeys(value, ['authPolicy', 'loginIssuance', 'maintenanceEnabled', 'assistantEnabled'], 'runtime config');
  if (value.authPolicy !== 'disabled'
    && value.authPolicy !== 'optional'
    && value.authPolicy !== 'required') {
    throw new Error('runtime config.authPolicy содержит неизвестную policy');
  }
  if (value.loginIssuance !== 'available'
    && value.loginIssuance !== 'disabled'
    && value.loginIssuance !== 'degraded') {
    throw new Error('runtime config.loginIssuance содержит неизвестное состояние');
  }
  if (value.maintenanceEnabled !== undefined && typeof value.maintenanceEnabled !== 'boolean') {
    throw new Error('runtime config.maintenanceEnabled должен быть boolean');
  }
  if (value.assistantEnabled !== undefined && typeof value.assistantEnabled !== 'boolean') {
    throw new Error('runtime config.assistantEnabled должен быть boolean');
  }
  return {
    authPolicy: value.authPolicy,
    loginIssuance: value.loginIssuance,
    ...(value.maintenanceEnabled === undefined ? {} : { maintenanceEnabled: value.maintenanceEnabled }),
    ...(value.assistantEnabled === undefined ? {} : { assistantEnabled: value.assistantEnabled }),
  };
}

export function parseAuthSessionProjection(value: unknown): AuthSessionProjection {
  if (!isRecord(value)) throw new Error('auth session projection должен быть объектом');
  assertOnlyKeys(
    value,
    ['authentication', 'accountStatus', 'profile', 'csrfToken'],
    'auth session projection',
  );
  if (value.authentication !== 'authenticated') {
    throw new Error('auth session projection.authentication содержит неизвестное состояние');
  }
  const csrfToken = parseBoundedString(
    value.csrfToken,
    'auth session projection.csrfToken',
    512,
  );

  if (value.accountStatus === 'pending_profile') {
    if (value.profile !== null) throw new Error('pending_profile не должен содержать profile');
    return {
      authentication: 'authenticated',
      accountStatus: 'pending_profile',
      profile: null,
      csrfToken,
    };
  }
  if (value.accountStatus === 'active') {
    if (!isRecord(value.profile)) {
      throw new Error('active auth session projection.profile должен быть объектом');
    }
    assertOnlyKeys(value.profile, ['displayName'], 'auth session projection.profile');
    const displayName = parseBoundedString(
      value.profile.displayName,
      'auth session projection.profile.displayName',
      96,
    );
    return {
      authentication: 'authenticated',
      accountStatus: 'active',
      profile: { displayName },
      csrfToken,
    };
  }
  throw new Error('auth session projection.accountStatus содержит неизвестный статус');
}

export function parseGoogleLoginIntentRequest(value: unknown): GoogleLoginIntentRequest {
  if (!isRecord(value)) throw new Error('google login intent request должен быть объектом');
  assertOnlyKeys(value, ['returnTo'], 'google login intent request');
  return { returnTo: parseAuthReturnTo(value.returnTo) };
}

export function parseGoogleLoginIntent(
  value: unknown,
  expectedPublicOrigin: string,
): GoogleLoginIntent {
  if (!isRecord(value)) throw new Error('google login intent должен быть объектом');
  assertOnlyKeys(value, ['clientId', 'loginUri', 'state', 'nonce', 'returnTo'], 'google login intent');
  let origin: URL;
  try {
    origin = new URL(expectedPublicOrigin);
  } catch {
    throw new Error('expected public origin должен быть exact origin');
  }
  if (origin.origin !== expectedPublicOrigin) {
    throw new Error('expected public origin должен быть exact origin');
  }
  const expectedLoginUri = `${expectedPublicOrigin}/api/auth/google/callback`;
  if (value.loginUri !== expectedLoginUri) {
    throw new Error('google login intent.loginUri не совпадает с exact callback URI');
  }
  return {
    clientId: parseBoundedString(value.clientId, 'google login intent.clientId', 255),
    loginUri: expectedLoginUri,
    state: parseBoundedString(value.state, 'google login intent.state', 512),
    nonce: parseBoundedString(value.nonce, 'google login intent.nonce', 512),
    returnTo: parseAuthReturnTo(value.returnTo),
  };
}

export function parseUpdateProfileRequest(value: unknown): UpdateProfileRequest {
  if (!isRecord(value)) throw new Error('update profile request должен быть объектом');
  assertOnlyKeys(value, ['displayName'], 'update profile request');
  return {
    displayName: parseBoundedString(value.displayName, 'update profile request.displayName', 96),
  };
}
